import { drugAction } from '@/src/api'
import { productSchema } from '@schema/product.schema'
import { FormInputField, Input, RadioBtn, SelectInputField } from '@ui'
import { Box, CheckBox, Text } from 'grommet'
import { Controller, useFormContext } from 'react-hook-form'
import * as yup from 'yup'

import { useAppSelector } from '@/src/store/hooks'
import { useImagePreviewStore } from '@/src/zustand/store'
import { Select } from '@ui/SearchInput/Select'
import { useEffect, useState } from 'react'

type ProductFormData = yup.InferType<typeof productSchema>
const dosageFormOptions = [
  { label: 'Tablet', value: 'Tablet' },
  { label: 'Capsule', value: 'Capsule' },
  { label: 'Injection', value: 'Injection' },
  { label: 'Suspension', value: 'Suspension' },
  { label: 'Ointment', value: 'Ointment' },
  { label: 'Vaccine (Vial)', value: 'Vaccine (Vial)' }
]
const routeOptions = [
  { label: 'Oral', value: 'Oral' },
  { label: 'IV', value: 'IV' },
  { label: 'IM', value: 'IM' },
  { label: 'SC', value: 'SC' },
  { label: 'Topical', value: 'Topical' }
]
const storageOptions = [
  { label: 'Room Temperature', value: 'Room Temperature' },
  { label: 'Refrigerate (2-8°C)', value: 'Refrigerate (2-8°C)' },
  { label: 'Protect from Light', value: 'Protect from Light' },
  { label: 'Flammable', value: 'Flammable' }
]

type Props = {
  step: number
  objectUrl?: string | null
  addon?: React.ReactNode
  createProd?: boolean
  //   methods?: typeof useForm<ProductFormData>
}

const GeneralInfoStep = ({ step, createProd }: Props) => {
  const {
    dosage_forms,
    routes,
    dosage_unit,
    unit_of_measurement,
    package: package_types,
    therapeutic_categories
  } = useAppSelector(state => state.config)
  const { watch, setValue, control } = useFormContext<ProductFormData>()
  const { imgFile, setImgFile } = useImagePreviewStore()
  const [objectUrl, setObjectUrl] = useState<string | null>(null)

  const cold_chain = watch('cold_chain')
  const vaccine = watch('vaccine')

  const t = therapeutic_categories?.map(cat => ({ label: cat, value: cat }))

  useEffect(() => {
    if (imgFile && typeof imgFile !== 'string') {
      const url = URL.createObjectURL(imgFile as Blob)
      setObjectUrl(url)
      return () => {
        try {
          URL.revokeObjectURL(url)
        } catch (e) {
          console.log(e)
        }
        setObjectUrl(null)
      }
    }

    // if imgFile is string or null, ensure objectUrl is cleared
    setObjectUrl(null)
    return () => {}
  }, [imgFile])

  // const apiEntries = useProductApiStore(state => state.entries)
  // useEffect(() => {
  //   const concat = apiEntries
  //     ?.map(e => `${(e.api_name || '').trim()}${e.strength ? ` ${e.strength.trim()}` : ''}`.trim())
  //     .filter(Boolean)
  //     .join(' + ')
  //   if (concat) setValue('generic_name', concat)
  // }, [apiEntries, setValue])

  const yesNoOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ]

  const YesOrNoRadio = ({
    name,
    label,
    required = false
  }: {
    name: keyof ProductFormData
    label: string
    required?: boolean
  }) => {
    return (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Box align="center" direction="row" gap="small" margin={{ bottom: 'small' }}>
            <Text style={{ fontSize: 14, minWidth: 180 }}>
              {label} {required && <span style={{ color: '#de2c21' }}>*</span>}
            </Text>
            {yesNoOptions?.map(opt => (
              <Box align="center" direction="row" gap="xsmall" key={String(opt.value)} margin={{ right: '18px' }}>
                <RadioBtn
                  checked={field.value === opt.value}
                  name={name}
                  onChange={() => field.onChange(opt.value)}
                  value={String(opt.value)}
                />
                <span style={{ fontSize: 13 }}>{opt.label}</span>
              </Box>
            ))}
          </Box>
        )}
      />
    )
  }

  return (
    <div
      className={`step-slide ${step === 1 ? 'active' : ''}`}
      style={{
        position: 'absolute',
        transform: `translateX(${step === 1 ? '0' : '-100%'})`,
        transition: 'all 0.3s ease-in-out',
        width: '100%'
      }}
    >
      {/* Step 1: General Information */}
      <Box className="!min-h-[auto]" direction="column" gap="medium">
        <Controller
          name="generic_name"
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            const selectedValues = value
              ? value
                  .split('/')
                  ?.map((val: string) => ({ label: val.trim(), value: val.trim() }))
                  .filter((item: any) => item.label)
              : []
            return (
              <Select
                borderRadius={'25px'}
                error={error?.message}
                getOptionLabel={option => option.label}
                getOptionValue={option => option.value}
                isAsync
                isCreatable
                isMulti
                label="Generic Name"
                loadOptions={drugAction}
                onChange={selectedOptions => {
                  const names = selectedOptions ? selectedOptions?.map((option: any) => option.value) : []
                  onChange(names.join('/'))
                }}
                placeholder="Generic Name"
                value={selectedValues}
              />
            )
          }}
        />
        <FormInputField label="Brand Name" name="brand_name" placeholder="Brand Name" />

        <SelectInputField label="Dosage Form" name="dosage_form" options={dosage_forms} placeholder="Dosage Form" />
        <SelectInputField
          label="Unit of Measurement (UoM)"
          name="inventory_unit"
          options={unit_of_measurement}
          placeholder="E.g. mg, ml"
        />
        <SelectInputField label="Route" name="route" options={routes} placeholder="Route" />
        <Controller
          control={control}
          name="therapeutic_category"
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            const selectedValues = value
              ? value
                  .split(' , ')
                  ?.map((val: string) => ({ label: val.trim(), value: val.trim() }))
                  .filter((item: any) => item.label)
              : []
            return (
              <Select
                borderRadius={'25px'}
                defaultOptions
                error={error?.message}
                getOptionLabel={option => option.label}
                getOptionValue={({ value }) => value}
                isMulti
                label="Therapeutic Category"
                onChange={selectedOptions => {
                  const names = selectedOptions ? selectedOptions?.map((option: any) => option.value) : []
                  onChange(names.join(' , '))
                }}
                options={t}
                placeholder="Therapeutic Category"
                value={selectedValues}
              />
            )
          }}
        />
        {/* <FormInputField
          label="Packaging "
          name="packaging"
          placeholder='e.g. "Blister pack of 10s", "Bottle of 100s"'
        /> */}
        <SelectInputField
          label="Storage Condition"
          name="storage_condition"
          options={storageOptions}
          placeholder="Storage Condition"
          valueKey={{ key: 'value', reduce: true }}
        />
        <Box direction="column" gap="small">
          <Text margin={{}} style={{ fontSize: '14px', fontWeight: 500 }}>
            Product Image (Optional)
          </Text>
          <Controller
            control={control}
            name="product_image"
            render={({ field: { value, onChange } }) => (
              <Box
                style={{
                  alignItems: 'center',
                  border: '1.5px solid #aeafb1',
                  borderRadius: 14,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  minHeight: 160,
                  position: 'relative',
                  width: '100%'
                }}
              >
                {!value && !imgFile ? (
                  <label
                    style={{
                      alignItems: 'center',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      justifyContent: 'center',
                      padding: '30px 0',
                      width: '100%'
                    }}
                  >
                    <Input
                      accept="image/*"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const file = e.target.files?.[0]
                        setImgFile(file || null)
                        onChange(file || null)
                      }}
                      style={{ display: 'none' }}
                      title="Product image"
                      type="file"
                    />
                    <Box
                      background="#F8F9FA"
                      hoverIndicator={{ color: '#f0f0f0' }}
                      pad="small"
                      style={{
                        border: '1px solid #ddd',
                        borderRadius: 8,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                        color: '#888',
                        fontWeight: 500,
                        textAlign: 'center',
                        transition: 'background 0.2s'
                      }}
                    >
                      <span style={{ color: '#666' }}>Click to upload image</span>
                    </Box>
                    <Text size="small" style={{ color: '#666', marginTop: 6 }}>
                      JPG, PNG or GIF. Max size 5MB.
                    </Text>
                  </label>
                ) : (
                  <Box align="center" direction="column" gap="small" justify="center">
                    <img
                      alt="Preview"
                      height={85}
                      src={
                        value && typeof value !== 'string'
                          ? URL.createObjectURL((value as unknown as File) || (imgFile as File))
                          : value && typeof value === 'string' && imgFile === null
                            ? value
                            : (objectUrl ?? '')
                      }
                      style={{
                        background: '#fff',
                        border: '1px solid #eee',
                        borderRadius: 10,
                        objectFit: 'cover'
                      }}
                      width={85}
                    />
                    <button
                      onClick={() => {
                        setImgFile(null)
                        onChange(null)
                      }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: '#888',
                        cursor: 'pointer',
                        fontSize: '13px',
                        marginTop: 4,
                        textDecoration: 'underline'
                      }}
                      type="button"
                    >
                      Remove Image
                    </button>
                  </Box>
                )}
              </Box>
            )}
          />
          <Box
            direction="column"
            gap="small"
            style={{
              background: '#fcfcfc',
              borderRadius: 8,
              margin: '0px',
              padding: 16
            }}
          >
            <YesOrNoRadio label="Is Product Cold-Chain?" name="cold_chain" required />
            {cold_chain && (
              <Box direction="column" gap="small" style={{ marginLeft: 20 }}>
                <Controller
                  control={control}
                  name="vaccine"
                  render={({ field }) => (
                    <Box align="center" direction="row">
                      <CheckBox
                        checked={!!field.value}
                        label="Vaccine?"
                        onChange={e => field.onChange(e.target.checked)}
                      />
                    </Box>
                  )}
                />
                <FormInputField label="Min Temp (°C)" name="min_temp" placeholder="Min Temp (°C)" type="number" />
                <FormInputField label="Max Temp (°C)" name="max_temp" placeholder="Max Temp (°C)" type="number" />
                {vaccine && (
                  <Box direction="column" gap="medium">
                    <FormInputField label="Antigen Type" name="vaccine_antigen_type" placeholder="Antigen Type" />
                    <FormInputField
                      label="Presentation Type"
                      name="vaccine_presentation_type"
                      placeholder="Presentation Type"
                    />
                  </Box>
                )}
              </Box>
            )}
            <YesOrNoRadio label="Is Product Controlled?" name="controlled_substance" required />
            <YesOrNoRadio label="Is Product Active?" name="is_active" required />
          </Box>
        </Box>
      </Box>
    </div>
  )
}

export default GeneralInfoStep
